import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Button, Modal } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { Drawer, DrawerContent, DrawerHeader } from 'components/drawer';
import { HorizontalDraggableDivider } from 'components/draggable-divider';
import type { WellboreMapData } from 'types/api-responses';
import { CurrentSuggestions } from './current-suggestions';
import { ReconcileActionBar } from './reconcile-action-bar';
import { AddWellsToReconcileTearsheet } from './add-wells-tearsheet';
import { PastVersionsList } from './past-versions-list';
import { WellConsolidationPanel } from './well-consolidation-panel';
import type { WellPastVersion } from './types';
import {
  assignments,
  groupOf,
  initWellConsolidation,
  reconcileSummary,
  statusOf,
  wellConsolidationReducer,
  type WellCandidate,
  type WellConsolidationAction,
  type WellConsolidationState,
  type WellReconcileAssignment,
} from './consolidation-utils';
import styles from './reconcile-wells-tearsheet.module.scss';

/**
 * The sheet's height, leaving the 3rem top gap the Add Wellbore tearsheet sits under —
 * the side gutters are the module's own `max-inline-size`. `size` is what the drawer
 * turns into a height, so the gap has to be subtracted here rather than in CSS.
 */
const SHEET_HEIGHT = 'calc(100% - 3rem)';

/** The result panel's starting width for the divider's keyboard steps (CSS default: 40%). */
const RIGHT_PANEL_WIDTH = 640;
const RIGHT_PANEL_MIN = 320;
const RIGHT_PANEL_MAX = 960;

/** Stable identity, so an absent `pastVersions` does not remount the list each render. */
const NO_PAST_VERSIONS: WellPastVersion[] = [];

/** Stable empty geometry for the catalog-added wells, until the operator adds any. */
const EMPTY_GEOMETRY: Record<string, WellboreMapData> = {};

/**
 * The consolidation model plus the sheet's own confirmation state. Keeping the two in
 * one reducer is what lets a reopen reset both in a single dispatch, rather than
 * clearing the confirmation through a setState side-channel in an effect.
 */
interface HostState {
  model: WellConsolidationState;
  /** The survivor key awaiting confirmation; doubles as the modal's open state. */
  confirmKey: string | null;
}

/** The consolidation actions plus the two host-only meta-actions. */
type HostAction =
  | WellConsolidationAction
  | { type: '__reinit'; state: WellConsolidationState }
  | { type: '__confirm'; key: string | null };

/**
 * The consolidation reducer, wrapped so the meta-actions are handled alongside it.
 * @param {HostState} state The current model and confirmation.
 * @param {HostAction} action The action to apply.
 * @return {HostState} The next state.
 */
const hostReducer = (state: HostState, action: HostAction): HostState => {
  if (action.type === '__reinit')
    return { model: action.state, confirmKey: null };
  if (action.type === '__confirm') return { ...state, confirmKey: action.key };
  return {
    model: wellConsolidationReducer(state.model, action),
    confirmKey: state.confirmKey,
  };
};

/**
 * The seed state for a pool: the consolidation seed, nothing awaiting confirmation.
 * @param {WellCandidate[]} pool The viewed well followed by its potential duplicates.
 * @return {HostState} The seed state.
 */
const initHost = (pool: WellCandidate[]): HostState => ({
  model: initWellConsolidation(pool),
  confirmKey: null,
});

export interface ReconcileWellsTearsheetProps {
  open: boolean;
  onClose: () => void;
  /** The viewed well's name, for the header — known before the pool has loaded. */
  anchorName: string;
  /** The viewed well's uwi, for the header. */
  anchorUwi: string;
  /** The viewed well followed by its potential duplicates. Order fixes map colours. */
  pool: WellCandidate[];
  /** Wellbore geometry per row id, for the inset map. */
  geometry: Record<string, WellboreMapData>;
  /** Records already reconciled into the viewed well. */
  pastVersions?: WellPastVersion[];
  /** The pool is still being resolved. */
  loading?: boolean;
  /** The pool could not be loaded (a candidates/details fetch failed). */
  loadError?: boolean;
  /** A reconcile is in flight; the action bar waits on it. */
  committing?: boolean;
  /** Why the last reconcile was refused, when one was. */
  commitError?: string | null;
  /** Whether reconcile is enabled in this environment; when off, the sheet is explorable but submit is
   *  disabled with an info notice. Defaults to true. */
  reconcileEnabled?: boolean;
  /**
   * Called with the structure to commit, survivor by survivor, and the success toast's
   * sentences — built here because only the sheet holds the catalog-added wells' names.
   */
  onReconcile: (
    committed: WellReconcileAssignment[],
    summary: string[]
  ) => void;
}

/**
 * Reconcile Wells — the viewed well and its suspected duplicates on the left, and the
 * survivor/dedupe structure the reconcile would commit on the right.
 *
 * Checking a record is what authors that structure: the first check seeds a survivor
 * and later checks fold into the most recent one. Nothing is retired until Reconcile is
 * pressed, and Reconcile stays disabled until at least one survivor actually has a
 * record to retire — so opening the sheet on a well with no duplicates commits nothing.
 * @param {ReconcileWellsTearsheetProps} props Open state, the pool, its geometry and the handlers.
 * @return {JSX.Element | null} The tearsheet.
 */
export function ReconcileWellsTearsheet({
  open,
  onClose,
  anchorName,
  anchorUwi,
  pool,
  geometry,
  pastVersions = NO_PAST_VERSIONS,
  loading = false,
  loadError = false,
  committing = false,
  commitError = null,
  reconcileEnabled = true,
  onReconcile,
}: ReconcileWellsTearsheetProps) {
  const [{ model, confirmKey }, dispatch] = useReducer(
    hostReducer,
    pool,
    initHost
  );

  // The stacked "Add wells to reconcile" catalog search, and the geometry of the wells it adds — the
  // prop `geometry` only covers the detector's suggestions.
  const [addOpen, setAddOpen] = useState(false);
  const [addedGeometry, setAddedGeometry] =
    useState<Record<string, WellboreMapData>>(EMPTY_GEOMETRY);

  // Reseed the sheet only when the pool's CONTENT changes (its rowId signature), not on every new array
  // reference: a background refetch returns the same suggestions in a fresh array, and reseeding on that
  // would discard catalog-added wells and all in-progress checking. The empty -> loaded transition still
  // changes the signature (so the first real pool seeds); a same-content refetch does not.
  const poolSignature = useMemo(
    () => pool.map((candidate) => candidate.rowId).join(','),
    [pool]
  );
  const seededSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededSignatureRef.current = null;
      return;
    }
    if (seededSignatureRef.current !== poolSignature) {
      dispatch({ type: '__reinit', state: initWellConsolidation(pool) });
      setAddedGeometry(EMPTY_GEOMETRY);
      seededSignatureRef.current = poolSignature;
    }
  }, [open, poolSignature, pool]);

  const committed = useMemo(() => assignments(model), [model]);
  // Null until the divider is first dragged, so the panel keeps its CSS default width.
  const [rightWidth, setRightWidth] = useState<number | null>(null);
  // The pool as the sheet works it: the fetched suggestions plus any wells added from the catalog.
  // Rendered instead of the prop pool so an added well shows in the table (and can be checked in).
  const displayPool = model.pool;
  // The map geometry for the shown pool: the fetched suggestions' plus the added wells' own geometry.
  const displayGeometry = useMemo(
    () => ({ ...geometry, ...addedGeometry }),
    [geometry, addedGeometry]
  );
  const pooledRowIds = useMemo(
    () => new Set(displayPool.map((candidate) => candidate.rowId)),
    [displayPool]
  );

  /**
   * Removal from either the Survivor toggle or the Actions column. Only a survivor that
   * has names folded beneath it needs confirming — everything else is reversible in a
   * click, and a modal on those would be noise.
   */
  const requestRemove = useCallback(
    (key: string) => {
      const hasDependents = model.dependents.some(
        (dep) => dep.survivorKey === key
      );
      if (statusOf(model, key) === 'survivor' && hasDependents) {
        dispatch({ type: '__confirm', key });
        return;
      }
      dispatch({ type: 'removeName', key });
    },
    [model]
  );

  // Swallow the close while the Add-wells sheet overlays this one (one Escape would unmount the whole
  // reconcile) or while committing (close lets the parent reset() the shared mutation, losing the toast).
  const closeSheet = useCallback(() => {
    if (!addOpen && !committing) onClose();
  }, [addOpen, committing, onClose]);

  // The sheet stays mounted while the pool resolves: it is opened on a well the table
  // already named, so blanking it until the records arrive would read as a failure.
  if (!open) return null;

  const confirmName = confirmKey ? groupOf(model, confirmKey)?.name : null;

  return (
    <Drawer
      lockBackgroundScroll
      open={open}
      direction="bottom"
      size={SHEET_HEIGHT}
      onClose={closeSheet}
      className={styles.tearsheet}
    >
      <DrawerHeader className={styles.header}>
        <h3 className={styles.title}>
          Reconcile Wells — {anchorName} · UWI {anchorUwi}
        </h3>
        <Button
          kind="ghost"
          size="md"
          hasIconOnly
          iconDescription="Close"
          tooltipPosition="left"
          renderIcon={(props) => <Close size={20} {...props} />}
          onClick={closeSheet}
        />
      </DrawerHeader>

      <DrawerContent className={styles.body}>
        <div className={styles.panels}>
          <CurrentSuggestions
            loading={loading}
            loadError={loadError}
            displayPool={displayPool}
            geometry={displayGeometry}
            checkedIds={model.checkedIds}
            onToggle={(rowId, checked) =>
              dispatch({ type: checked ? 'check' : 'uncheck', id: rowId })
            }
            onToggleAll={(checked) =>
              dispatch({ type: checked ? 'checkAll' : 'uncheckAll' })
            }
            onAddWells={() => setAddOpen(true)}
          />
          <HorizontalDraggableDivider
            side="right"
            minWidth={RIGHT_PANEL_MIN}
            maxWidth={RIGHT_PANEL_MAX}
            value={rightWidth ?? RIGHT_PANEL_WIDTH}
            onDrag={setRightWidth}
            label="Resize result panel"
            className={styles.divider}
          />

          <section
            className={styles.right}
            aria-label="Well Consolidation"
            style={rightWidth ? { flex: `0 0 ${rightWidth}px` } : undefined}
          >
            <WellConsolidationPanel
              state={model}
              dispatch={dispatch}
              onRequestRemove={requestRemove}
            />

            <PastVersionsList
              anchorName={anchorName}
              pastVersions={pastVersions}
            />
          </section>
        </div>
      </DrawerContent>

      <ReconcileActionBar
        commitError={commitError}
        committing={committing}
        canReconcile={committed.length > 0}
        reconcileEnabled={reconcileEnabled}
        onCancel={closeSheet}
        onReconcile={() =>
          onReconcile(committed, reconcileSummary(committed, model.pool))
        }
      />

      <Modal
        open={confirmKey !== null}
        danger
        modalHeading={`Remove survivor "${confirmName ?? ''}"?`}
        primaryButtonText="Remove Survivor"
        secondaryButtonText="Cancel"
        onRequestClose={() => dispatch({ type: '__confirm', key: null })}
        onRequestSubmit={() => {
          if (confirmKey) dispatch({ type: 'removeName', key: confirmKey });
          dispatch({ type: '__confirm', key: null });
        }}
      >
        <p>
          The names folded beneath it revert to unactioned and their records are
          unchecked. Nothing is retired until Reconcile is clicked.
        </p>
      </Modal>
      {/* Mounted only while open: it runs the catalog-search query, so keeping it mounted would need
          a QueryClient even when closed. */}
      {addOpen && (
        <AddWellsToReconcileTearsheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          anchorName={anchorName}
          anchorUwi={anchorUwi}
          existingRowIds={pooledRowIds}
          onAdd={(candidates, geometryByRowId) => {
            dispatch({ type: 'addWells', candidates });
            setAddedGeometry((prev) => ({ ...prev, ...geometryByRowId }));
          }}
        />
      )}
    </Drawer>
  );
}
